import React from 'react';
import CourseTemplate from '../../components/CourseTemplate';

const OptometryCoursePage: React.FC = () => {
  const optometryData = {
    title: "Bachelor of Optometry",
    shortName: "B.Optom",
    duration: "4 Years",
    description: "A comprehensive program focused on eye care, vision correction, and the diagnosis and management of eye diseases and visual disorders.",
    overview: "Bachelor of Optometry is a specialized healthcare program that prepares students to become eye care professionals. The course provides extensive knowledge about the anatomy and physiology of the eye, visual system disorders, and various diagnostic and therapeutic techniques. Students learn to conduct comprehensive eye examinations, prescribe corrective lenses, detect eye diseases, and provide pre- and post-operative care. The program combines theoretical knowledge with extensive practical training in modern optometric equipment and techniques.",
    eligibility: [
      "Completed 10+2 with Physics, Chemistry, and Biology",
      "Minimum 50% aggregate marks (45% for SC/ST/OBC)",
      "Age: 17-25 years",
      "Must pass entrance examination (varies by institution)",
      "Medical fitness certificate required",
      "English proficiency essential"
    ],
    careerProspects: {
      roles: [
        "Optometrist",
        "Eye Care Specialist",
        "Vision Therapist",
        "Contact Lens Specialist",
        "Low Vision Rehabilitation Specialist",
        "Pediatric Optometrist",
        "Geriatric Eye Care Specialist",
        "Research Optometrist",
        "Clinical Optometrist",
        "Optical Shop Manager"
      ],
      industries: [
        "Hospitals and Clinics",
        "Eye Care Centers",
        "Optical Retail Chains",
        "Research Institutions",
        "Government Health Services",
        "Vision Therapy Centers",
        "Contact Lens Companies",
        "Pharmaceutical Companies",
        "Educational Institutions",
        "Private Practice"
      ],
      averageSalary: "₹3-8 LPA (varies by experience and specialization)",
      topRecruiters: [
        "Aravind Eye Care System",
        "LV Prasad Eye Institute",
        "Sankara Nethralaya",
        "Apollo Hospitals",
        "Fortis Healthcare",
        "Lenskart",
        "Titan Eye Plus",
        "Government Hospitals",
        "Vision Express",
        "Carl Zeiss India"
      ]
    },
    curriculum: [
      {
        year: 1,
        subjects: [
          "General Anatomy",
          "General Physiology",
          "Biochemistry",
          "Physics and Optics",
          "Mathematics",
          "English Communication",
          "Computer Applications",
          "Introduction to Optometry"
        ]
      },
      {
        year: 2,
        subjects: [
          "Ocular Anatomy",
          "Ocular Physiology",
          "Geometric Optics",
          "Visual Optics",
          "Microbiology",
          "Pathology",
          "Pharmacology",
          "Optometric Instrumentation"
        ]
      },
      {
        year: 3,
        subjects: [
          "Clinical Optometry",
          "Binocular Vision",
          "Contact Lenses",
          "Low Vision",
          "Pediatric Optometry",
          "Geriatric Optometry",
          "Systemic Diseases",
          "Practice Management"
        ]
      },
      {
        year: 4,
        subjects: [
          "Advanced Clinical Optometry",
          "Therapeutics",
          "Public Health Optometry",
          "Research Methodology",
          "Community Eye Care",
          "Internship/Clinical Training",
          "Project Work",
          "Ethics and Jurisprudence"
        ]
      }
    ],
    admissionProcess: [
      "Apply to optometry colleges offering B.Optom",
      "Appear for entrance examination (if required)",
      "Merit-based selection process",
      "Document verification",
      "Medical fitness examination",
      "Fee payment and admission confirmation",
      "Orientation program attendance",
      "Begin academic session"
    ],
    topColleges: [
      {
        name: "Elite School of Optometry",
        location: "Chennai, Tamil Nadu",
        fees: "₹2.5 LPA",
        ranking: "Top Private College"
      },
      {
        name: "Bausch & Lomb School of Optometry",
        location: "Hyderabad, Telangana",
        fees: "₹3 LPA",
        ranking: "Industry Affiliated"
      },
      {
        name: "Lotus College of Optometry",
        location: "Mumbai, Maharashtra",
        fees: "₹2.8 LPA",
        ranking: "Well Established"
      },
      {
        name: "Gandhi Eye Hospital School of Optometry",
        location: "Aligarh, Uttar Pradesh",
        fees: "₹2.2 LPA",
        ranking: "Government Affiliated"
      },
      {
        name: "Manipal College of Allied Health Sciences",
        location: "Manipal, Karnataka",
        fees: "₹4 LPA",
        ranking: "Deemed University"
      }
    ],
    skills: [
      "Eye Examination Techniques",
      "Visual Acuity Testing",
      "Refraction Skills",
      "Contact Lens Fitting",
      "Binocular Vision Assessment",
      "Low Vision Rehabilitation",
      "Patient Communication",
      "Equipment Operation",
      "Clinical Documentation",
      "Problem Solving",
      "Attention to Detail",
      "Manual Dexterity"
    ],
    futureScope: "The field of optometry is experiencing rapid growth due to increasing screen time, aging population, and growing awareness of eye health. Future opportunities include specialization in areas like myopia control, dry eye management, and advanced contact lens fitting. Emerging technologies like AI-assisted diagnostics, teleoptometry, and advanced imaging systems are creating new career paths. The integration of optometry with telemedicine and digital health platforms offers exciting possibilities for remote eye care delivery.",
    faqs: [
      {
        question: "What is the difference between an Optometrist and an Ophthalmologist?",
        answer: "Optometrists are primary eye care professionals who examine, diagnose, and treat vision problems and eye diseases. Ophthalmologists are medical doctors who can perform eye surgeries and treat complex eye conditions. Optometrists refer patients to ophthalmologists when surgical intervention is needed."
      },
      {
        question: "Can I start my own practice after B.Optom?",
        answer: "Yes, after completing B.Optom and obtaining the necessary licenses, you can start your own optometry practice or optical shop. Many optometrists successfully run independent practices or franchise outlets."
      },
      {
        question: "What are the career advancement opportunities?",
        answer: "Career advancement includes specialization through diploma/certificate courses, pursuing M.Optom, becoming a consultant, opening multiple practice locations, or moving into teaching and research roles in optometry institutions."
      },
      {
        question: "Is there scope for further education after B.Optom?",
        answer: "Yes, you can pursue Master of Optometry (M.Optom), PhD in Optometry, or specialized diplomas in areas like contact lenses, low vision, or pediatric optometry."
      },
      {
        question: "What is the job market like for optometrists?",
        answer: "The job market is excellent with high demand in both urban and rural areas. Government initiatives for eye care and the growing optical retail industry have created numerous employment opportunities."
      },
      {
        question: "Do I need to register with any council after graduation?",
        answer: "Yes, optometrists need to register with the respective state optometry councils and obtain necessary licenses to practice. Some states also require continuing education for license renewal."
      }
    ]
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-black flex flex-col items-center justify-start py-8 px-2">
      <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 drop-shadow-glow mb-6 text-center animate-glow">
        {optometryData.title} ({optometryData.shortName})
      </h1>
      <div className="w-full max-w-4xl">
        <CourseTemplate courseData={optometryData} />
      </div>
    </div>
  );
};

export default OptometryCoursePage;